import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { getCheckoutStatus } from '@/lib/api';
import { LUXE } from '@/constants/testIds';

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

const BookingSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | failed | expired
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('failed');
      return;
    }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const status = await getCheckoutStatus(sessionId);
        setData(status);
        if (status.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (status.status === 'expired') {
          setState('expired');
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState('failed');
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        console.error(err);
        setState('failed');
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 min-h-[80vh] flex items-center">
      <div className="luxe-container max-w-2xl mx-auto">
        <div className="luxe-card p-10 sm:p-14 text-center">
          {state === 'polling' && (
            <div data-testid={LUXE.paymentStatusPending}>
              <Loader2 size={48} className="text-[#C9A24A] mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl text-white mb-2 font-serif">Verifying Payment</h1>
              <div className="gold-divider mx-auto my-4" />
              <p className="text-white/60">
                Just a moment while we confirm your deposit with Stripe...
              </p>
            </div>
          )}

          {state === 'paid' && (
            <div data-testid={LUXE.paymentStatusPaid}>
              <CheckCircle2 size={56} className="text-[#C9A24A] mx-auto mb-6" />
              <h1 className="text-3xl sm:text-4xl text-white mb-3 font-serif">
                Deposit <span className="italic text-[#C9A24A]">Received</span>
              </h1>
              <div className="gold-divider mx-auto my-4" />
              <p className="text-white/70 mb-2">
                Thanks — your appointment is now secured.
              </p>
              {data?.amount_total != null && (
                <p className="text-white/80 mb-6">
                  Amount charged:{' '}
                  <span className="text-[#C9A24A] font-serif text-xl">
                    ${(data.amount_total / 100).toFixed(2)} {data.currency?.toUpperCase()}
                  </span>
                </p>
              )}
              <p className="text-white/50 text-sm mb-8">
                We'll email you a confirmation once the studio reviews your
                booking. Balance is due at the appointment.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link to="/" data-testid="post-payment-home" className="btn-book">
                  Back to Home <ArrowRight size={14} />
                </Link>
                <Link
                  to="/booking"
                  data-testid="post-payment-new"
                  className="btn-outline-gold"
                >
                  Book Another
                </Link>
              </div>
            </div>
          )}

          {(state === 'failed' || state === 'expired') && (
            <div data-testid={LUXE.paymentStatusFailed}>
              <XCircle size={48} className="text-[#e57373] mx-auto mb-6" />
              <h1 className="text-3xl text-white mb-2 font-serif">
                {state === 'expired' ? 'Session Expired' : 'Payment Not Verified'}
              </h1>
              <div className="gold-divider mx-auto my-4" />
              <p className="text-white/60 mb-8">
                {state === 'expired'
                  ? 'Your checkout session timed out. You can retry from your booking.'
                  : 'We couldn\'t verify the payment yet. If you completed it, please check your email for a Stripe receipt or contact us.'}
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link to="/booking" data-testid="payment-retry" className="btn-book">
                  Try Again
                </Link>
                <Link
                  to="/contact"
                  data-testid="payment-contact"
                  className="btn-outline-gold"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
